import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Edit, Trash2, Star, Music, Users, User, PoundSterling } from "lucide-react";
import { useLocation } from "wouter";
import { CardActionsDropdown } from "@/components/card-actions-dropdown";
import type { ActWithParticipants } from "@shared/schema";

interface ActCardProps {
  act: ActWithParticipants;
  organisationId: number;
  onEdit: () => void;
  onDelete: () => void;
}

export function ActCard({ act, organisationId, onEdit, onDelete }: ActCardProps) {
  const [, setLocation] = useLocation();
  // Get display name for the act
  const getDisplayName = () => {
    if (act.name) return act.name;
    if (act.participants?.length > 0) {
      const primaryParticipant = act.participants.find(p => p.isPrimary);
      return primaryParticipant?.participant.name || act.participants[0]?.participant.name || "Unnamed Act";
    }
    return "Unnamed Act";
  };

  // Get act type icon
  const getActTypeIcon = () => {
    switch (act.actType) {
      case "individual":
        return <User className="h-4 w-4" />;
      case "duo":
        return <Users className="h-4 w-4" />;
      case "band":
      case "ensemble":
        return <Music className="h-4 w-4" />;
      default:
        return <User className="h-4 w-4" />;
    }
  };

  return (
    <Card 
      className="bg-white/20 backdrop-blur-xl border-white/30 hover:bg-white/30 transition-all duration-300 group cursor-pointer"
      onClick={(e) => {
        // Don't navigate if clicking on buttons
        if ((e.target as HTMLElement).closest('button')) return;
        setLocation(`/organisations/${organisationId}/acts/${act.id}/summary`);
      }}>
      <CardHeader>
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <CardTitle className="text-lg font-semibold text-slate-900 flex items-center gap-2">
              {getDisplayName()}
              {act.name && act.participants?.length > 0 && (
                <Badge variant="outline" className="text-xs">
                  {act.participants.length} {act.participants.length === 1 ? 'member' : 'members'}
                </Badge>
              )}
            </CardTitle>
            <CardDescription className="mt-1">
              <div className="flex items-center gap-2">
                {getActTypeIcon()}
                <span className="capitalize">{act.actType}</span>
                {act.rating && (
                  <div className="flex items-center gap-1">
                    <Star className="h-3 w-3 fill-yellow-400 text-yellow-400" />
                    <span className="text-xs">{act.rating}</span>
                  </div>
                )}
              </div>
            </CardDescription>
          </div>
          {/* Desktop: Individual buttons */}
          <div className="hidden sm:flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
            <Button
              size="sm"
              variant="ghost"
              onClick={onEdit}
              className="h-8 w-8 p-0"
            >
              <Edit className="h-4 w-4" />
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={onDelete}
              className="h-8 w-8 p-0 text-red-600 hover:text-red-700"
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
          {/* Mobile: Dropdown menu */}
          <div className="sm:hidden">
            <CardActionsDropdown
              onEdit={onEdit}
              onDelete={onDelete}
            />
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {/* Members list for acts with custom names */}
        {act.name && act.participants && act.participants.length > 0 && (
          <div>
            <p className="text-xs font-medium text-slate-600 mb-1">Members:</p>
            <div className="flex flex-wrap gap-1">
              {act.participants.map((ap) => (
                <Badge 
                  key={ap.participantId} 
                  variant="secondary" 
                  className="text-xs bg-white/50"
                >
                  {ap.participant.name}
                  {ap.role && ` (${ap.role})`}
                </Badge>
              ))}
            </div>
          </div>
        )}

        {/* Genres */}
        {act.genre && act.genre.length > 0 && (
          <div className="flex flex-wrap gap-1">
            {act.genre.map((g) => (
              <Badge key={g} variant="outline" className="text-xs">
                {g}
              </Badge>
            ))}
          </div>
        )}

        {/* Fee range */}
        {(act.minimumFee || act.preferredFee) && (
          <div className="flex items-center gap-1 text-sm text-slate-600">
            <PoundSterling className="h-3 w-3" />
            {act.minimumFee && (
              <span>£{(act.minimumFee / 100).toFixed(0)}</span>
            )}
            {act.minimumFee && act.preferredFee && <span>-</span>}
            {act.preferredFee && (
              <span>£{(act.preferredFee / 100).toFixed(0)}</span>
            )}
          </div>
        )}

        {/* Bio preview */}
        {act.bio && (
          <p className="text-sm text-slate-600 line-clamp-2">
            {act.bio}
          </p>
        )}

        {/* Links */}
        <div className="flex gap-2">
          {act.website && (
            <a
              href={act.website}
              target="_blank"
              rel="noopener noreferrer"
              className="text-xs text-blue-600 hover:underline"
            >
              Website
            </a>
          )}
          {act.spotifyUrl && (
            <a
              href={act.spotifyUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="text-xs text-green-600 hover:underline"
            >
              Spotify
            </a>
          )}
          {act.youtubeUrl && (
            <a
              href={act.youtubeUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="text-xs text-red-600 hover:underline"
            >
              YouTube
            </a>
          )}
        </div>
      </CardContent>
    </Card>
  );
}